import { Link } from "@tanstack/react-router";
import { TrackRecordCallout } from "@/components/site/article/TrackRecordCallout";
import { BrandedShortPlayer } from "@/components/site/article/BrandedShortPlayer";
import fvgDiagramAsset from "@/assets/articles/fair-value-gap-xauusd-diagram.jpg.asset.json";
import fvgDiagramSvgAsset from "@/assets/articles/fair-value-gap-xauusd-diagram.svg.asset.json";
const fvgDiagram = fvgDiagramAsset.url;

export function FairValueGapXauusdGuideContent() {
  return (
    <div className="article-body">
      <figure className="my-2 overflow-hidden rounded-xl border border-border/60">
        <img
          src={fvgDiagramSvgAsset.url}
          alt="Bullish Fair Value Gap on a XAU/USD 15 minute chart: sell side liquidity sweep, break of structure, FVG zone with the 50 percent entry, stop below the swept low and buy side liquidity target"
          width={1600}
          height={900}
          loading="eager"
          decoding="async"
          className="h-auto w-full"
        />
        <figcaption className="bg-card px-4 py-2 text-xs text-muted-foreground">
          Illustrative M15 XAU/USD sequence: sell side sweep, displacement with BOS, Fair Value Gap, 50 percent (CE) entry, stop beyond the swept low, buy side liquidity target.
        </figcaption>
      </figure>

      <h2 id="introduction">What a Fair Value Gap Really Is</h2>
      <p>
        <strong>
          A Fair Value Gap on XAUUSD is a three candle price zone where the wicks of
          candle 1 and candle 3 do not overlap, left by an aggressive displacement
          candle. It is tradable only when four checks pass: strong displacement, no
          wick overlap, a liquidity sweep before the gap, and an entry near the 50
          percent Consequent Encroachment level.
        </strong>
      </p>
      <ol>
        <li><strong>Displacement:</strong> the middle candle closes with a large body that breaks structure.</li>
        <li><strong>No wick overlap:</strong> candle 1 and candle 3 leave a clean, untraded range.</li>
        <li><strong>Prior sweep:</strong> equal highs, equal lows or a session extreme were taken first.</li>
        <li><strong>Consequent Encroachment:</strong> the 50 percent level of the gap balances entry against invalidation.</li>
      </ol>
      <p>
        A <strong>Fair Value Gap (FVG)</strong> is a three candle imbalance where price
        moved so aggressively that the wicks of candle 1 and candle 3 never overlap. The
        body of candle 2 leaves an unfilled vertical zone on the chart. That zone is the
        FVG, and on XAU/USD it is one of the clearest footprints of institutional order
        flow.
      </p>
      <p>
        Large participants cannot fill size at a single price. When they push gold
        several dollars in one 15 minute candle, they leave behind one sided delivery.
        Price frequently returns to rebalance that zone before continuing. That return
        is the trade, but only when the context around the gap qualifies it.
      </p>
      <p>
        In a bullish FVG, the zone runs from the <strong>high of candle 1</strong> to the
        <strong> low of candle 3</strong>. In a bearish FVG, it runs from the low of
        candle 1 to the high of candle 3. The midpoint of that zone is called the
        Consequent Encroachment (CE) level.
      </p>

      <div className="my-8 rounded-lg border border-gold/30 bg-gold-muted/30 p-5 text-sm leading-relaxed text-foreground/85">
        <strong className="text-gold">Pro Tip:</strong> An FVG without a prior liquidity
        sweep is just a gap. An FVG that forms <em>after</em> a sweep of equal highs or
        equal lows is a qualified point of interest. Always check the candle that
        created the imbalance: it should be the candle that broke structure.
      </div>

      <BrandedShortPlayer
        videoId="PQiI2TmVhBY"
        title="Why 80% of Fair Value Gaps Fail on Gold"
        label="Desk Video · 66s"
        points={[
          "Prior liquidity sweep: the impulse must purge BSL or SSL first.",
          "Confirmed body close beyond the swing, never a wick.",
          "Location: long FVGs in discount, premium gaps are targets.",
        ]}
      />

      <h2 id="how-to-spot">How to Spot a Valid FVG on Gold</h2>
      <p>
        On a 15 minute XAU/USD chart, walk through the last leg of impulsive movement
        and tick four boxes:
      </p>
      <ul>
        <li>
          <strong>Three consecutive candles</strong>, with the middle one being the
          displacement candle: a large body closing near its extreme.
        </li>
        <li>
          <strong>No wick overlap</strong> between candle 1 and candle 3. If they touch
          even by a tick, it is not a valid FVG.
        </li>
        <li>
          <strong>The displacement broke structure</strong> with a candle body close
          beyond a prior swing high or low. A wick through the level is a sweep, not a{" "}
          <Link to="/education/$slug" params={{ slug: "what-is-smart-money-concepts" }}>Break of Structure</Link>.
        </li>
        <li>
          <strong>Liquidity was swept</strong> immediately before the displacement:
          equal highs, equal lows, or a session high or low. See the{" "}
          <Link to="/education/$slug" params={{ slug: "liquidity-sweep-gold-trading" }}>liquidity sweep guide</Link>{" "}
          for how to read the raid.
        </li>
      </ul>
      <p>
        If all four boxes are ticked, you have an institutional FVG. If only the first
        two are present, you have a retail gap that price may ignore entirely.
      </p>

      <h2 id="fvg-quality">Which FVGs Deserve Attention</h2>
      <p>
        Not every valid gap carries the same weight. Rank them before you plan an entry:
      </p>
      <ul>
        <li>
          <strong>Timeframe:</strong> H4 and H1 gaps outrank M15 gaps. An M15 FVG nested
          inside an H1 FVG is the strongest alignment.
        </li>
        <li>
          <strong>Session:</strong> gaps printed during the London open or the New York
          open carry real participation. Gaps from thin Asian hours are lower quality.
        </li>
        <li>
          <strong>Freshness:</strong> an untouched FVG is preferred. Once price has
          traded through the CE level, the gap is mitigated and loses most of its value.
        </li>
        <li>
          <strong>Origin:</strong> a gap that also sits next to a qualified{" "}
          <Link to="/education/$slug" params={{ slug: "order-blocks-fvg-explained" }}>Order Block</Link>{" "}
          adds confluence. A gap floating in the middle of a range adds very little.
        </li>
      </ul>

      <h2 id="trading-fvg">Trading the FVG on XAU/USD</h2>
      <p>
        The execution flow is mechanical. Bias is set on H4. The trigger comes on M15.
        The entry sits at the FVG.
      </p>
      <ol>
        <li>
          Mark H4 bias from the last Break of Structure. If H4 is bullish, you only
          trade bullish FVGs on M15.
        </li>
        <li>
          Wait for M15 price to sweep a clean pool of sell side liquidity (equal lows,
          previous day low, Asia low).
        </li>
        <li>
          Look for an M15 Change of Character followed by a displacement candle that
          leaves an FVG.
        </li>
        <li>
          Set a limit order at the 50 percent mark of the FVG. Stop loss goes beyond the
          swept low, not beyond the gap edge. Targets are the next pool of buy side
          liquidity.
        </li>
      </ol>
      <p>
        The 50 percent level is a balanced entry: it improves the price compared with
        the top of the gap, while keeping the order inside the imbalance before deeper
        invalidation. If price closes a body through the far side of the FVG, the setup
        is void.
      </p>

      <h2 id="worked-example">Worked Example: A Bullish FVG on M15</h2>
      <p>
        The diagram above shows the sequence with illustrative prices. Treat the numbers
        as a template for the calculation, not as a recorded trade.
      </p>
      <ul>
        <li><strong>Context:</strong> H4 structure bullish, London session.</li>
        <li><strong>Sweep:</strong> M15 equal lows near 2,409.5 taken, then rejected.</li>
        <li><strong>Displacement:</strong> one large candle closes above the prior swing high at 2,419 (BOS).</li>
        <li><strong>FVG:</strong> candle 1 high 2,416.5, candle 3 low 2,421.6. CE level at about 2,419.0.</li>
        <li><strong>Entry:</strong> limit buy at 2,419.0.</li>
        <li><strong>Stop:</strong> 2,408.0, below the swept low. Risk is 11 dollars per ounce, which is 1R.</li>
        <li><strong>Target 1:</strong> buy side liquidity at 2,430.0, which equals 1R.</li>
        <li><strong>Target 2:</strong> the next H1 liquidity pool near 2,441.0, which equals 2R.</li>
      </ul>
      <p>
        The stop is wide by retail standards, and that is deliberate. Gold regularly
        trades through part of an FVG before reacting, so a stop at the gap edge is
        exposed to normal noise. Size the position to the stop distance, never the
        other way around.
      </p>

      <h2 id="common-mistakes">Common Mistakes That Kill the Edge</h2>
      <p>
        Most traders see FVGs everywhere and trade none of them well. The repeating
        mistakes:
      </p>
      <ul>
        <li>
          <strong>Trading FVGs against H4 bias.</strong> A clean bullish FVG on M15
          inside a bearish H4 leg is usually a pause before continuation, not a reversal.
        </li>
        <li>
          <strong>Skipping the liquidity sweep.</strong> Without a sweep, the gap has no
          evidence that resting orders were taken. It is a lower quality setup and
          should be treated as one.
        </li>
        <li>
          <strong>Stop loss too tight.</strong> Place the stop beyond the swept low or
          high, not just beyond the FVG edge.
        </li>
        <li>
          <strong>Entering mitigated gaps.</strong> A zone that has already been traded
          through has spent its imbalance.
        </li>
        <li>
          <strong>Ignoring the calendar.</strong> CPI, NFP and FOMC releases can blow
          through any gap. Stand aside around high impact events.
        </li>
      </ul>

      <h2 id="wrap-up">Wrap-Up</h2>
      <p>
        The FVG is not magic. It is a measurable inefficiency that price often returns
        to rebalance. On XAU/USD, with its deep participation and clean session
        liquidity, a qualified FVG gives a precise entry with a logical invalidation.
        Filter with H4 bias, demand a sweep and a body close BOS, execute at the 50
        percent level, and skip anything that does not pass all four boxes. Missing a
        setup costs nothing. Forcing one does.
      </p>
      <p>
        To see how a selective, rules based execution model performs over time, review
        the <Link to="/track-record">audited performance record</Link> and the
        individual trade replays in <Link to="/case-studies">Case Studies</Link>.
      </p>

      <TrackRecordCallout
        context="An imbalance model is worth studying only when its outcomes are published in full across multiple years."
        anchor="Review the audited XAUUSD performance record"
      />
    </div>
  );
}

export { fvgDiagram as fairValueGapDiagramImage };
